/**
 * Admin — tenant management for the admin role: create users, configure the
 * product categories that drive Technology Alignment scoring, and issue/list
 * per-client API keys for the CRM read API (raw key shown once).
 */
import { useEffect, useState, type FormEvent } from 'react';
import type { ProductCategory, User } from '@aurora/shared';
import { api, ApiClientError } from '../api/client';

export function Admin(): JSX.Element {
  return (
    <div className="admin-page">
      <div className="page-head">
        <h1>Admin</h1>
      </div>
      <UsersSection />
      <ProductCategoriesSection />
      <ApiKeysSection />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

function UsersSection(): JSX.Element {
  const [users, setUsers] = useState<User[]>([]);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'admin' | 'manager' | 'rep'>('rep');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async (): Promise<void> => {
    try {
      const res = await api.listUsers();
      setUsers(res.users);
    } catch (e) {
      setError(
        e instanceof ApiClientError ? e.message : 'Failed to load users',
      );
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const onCreate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.createUser({ email, password, fullName, role });
      setEmail('');
      setFullName('');
      setPassword('');
      setRole('rep');
      await load();
    } catch (err) {
      setError(
        err instanceof ApiClientError ? err.message : 'Failed to create user',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h2>Users</h2>
      <table className="admin-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id}>
              <td>{u.fullName}</td>
              <td>{u.email}</td>
              <td>{u.role}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <form className="inline-form" onSubmit={onCreate}>
        <input
          placeholder="Full name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          required
        />
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="Password (min 8)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <select
          value={role}
          onChange={(e) =>
            setRole(e.target.value as 'admin' | 'manager' | 'rep')
          }
        >
          <option value="rep">rep</option>
          <option value="manager">manager</option>
          <option value="admin">admin</option>
        </select>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          Add user
        </button>
      </form>
      {error && <div className="form-error">{error}</div>}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Product categories (Technology Alignment)
// ---------------------------------------------------------------------------

function ProductCategoriesSection(): JSX.Element {
  const [rows, setRows] = useState<ProductCategory[]>([]);
  const [name, setName] = useState('');
  const [keywords, setKeywords] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const add = (): void => {
    if (!name.trim()) return;
    setRows((r) => [
      ...r,
      {
        name: name.trim(),
        topicKeywords: keywords
          .split(',')
          .map((k) => k.trim())
          .filter(Boolean),
      },
    ]);
    setName('');
    setKeywords('');
  };

  const removeAt = (idx: number): void => {
    setRows((r) => r.filter((_, i) => i !== idx));
  };

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.updateProductCategories(rows);
      setNotice('Product categories saved.');
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h2>Product categories</h2>
      <p className="muted">
        These drive the Technology Alignment score: a lead's research topics are
        matched against each category's keywords.
      </p>
      <ul className="category-list">
        {rows.map((c, idx) => (
          <li key={`${c.name}-${idx}`}>
            <strong>{c.name}</strong>
            <span className="muted">
              {' '}
              — {c.topicKeywords.join(', ') || 'no keywords'}
            </span>
            <button
              type="button"
              className="btn btn-small"
              onClick={() => removeAt(idx)}
            >
              Remove
            </button>
          </li>
        ))}
        {rows.length === 0 && (
          <li className="muted">No product categories configured.</li>
        )}
      </ul>
      <div className="inline-form">
        <input
          placeholder="Category name (e.g. Single-cell sequencing)"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          placeholder="Keywords (comma-separated)"
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
        />
        <button type="button" className="btn" onClick={add}>
          Add
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={save}
          disabled={busy}
        >
          {busy ? 'Saving…' : 'Save categories'}
        </button>
      </div>
      {notice && <div className="form-notice">{notice}</div>}
      {error && <div className="form-error">{error}</div>}
    </section>
  );
}

// ---------------------------------------------------------------------------
// API keys (CRM read API)
// ---------------------------------------------------------------------------

function ApiKeysSection(): JSX.Element {
  const [keys, setKeys] = useState<
    Array<{ id: string; name: string; prefix: string }>
  >([]);
  const [name, setName] = useState('');
  const [rawKey, setRawKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async (): Promise<void> => {
    try {
      const res = await api.listApiKeys();
      setKeys(res.apiKeys);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to load keys');
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const onCreate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setRawKey(null);
    try {
      const res = await api.createApiKey(name);
      setRawKey(res.rawKey);
      setName('');
      await load();
    } catch (err) {
      setError(
        err instanceof ApiClientError ? err.message : 'Failed to create key',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h2>CRM API keys</h2>
      <ul className="category-list">
        {keys.map((k) => (
          <li key={k.id}>
            <strong>{k.name}</strong>
            <span className="muted"> — {k.prefix}…</span>
          </li>
        ))}
        {keys.length === 0 && <li className="muted">No API keys issued.</li>}
      </ul>
      {rawKey && (
        <div className="form-notice">
          New key (shown once — copy it now): <code>{rawKey}</code>
        </div>
      )}
      <form className="inline-form" onSubmit={onCreate}>
        <input
          placeholder="Key name (e.g. Salesforce prod)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <button type="submit" className="btn btn-primary" disabled={busy}>
          Issue key
        </button>
      </form>
      {error && <div className="form-error">{error}</div>}
    </section>
  );
}
